import { arrayUnion, doc, increment, updateDoc } from "firebase/firestore";
import { shuffle } from "lodash";
import { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { db } from "../../firebase";
import toast from "react-hot-toast";
import { formatTime } from "../../App";
import deepCopy from "../hooks/deepCopy";
import { halligalli } from "../../util/gameDefault";

const HalligalliHooks = () => {
  const {
    halligalli: { halligalliUsers, turnUser, turnStep, bellPress, gameStatus },
    roomId,
    message,
  } = useSelector(state => state.GameSlice);
  const { userId, nickname } = useSelector(state => state.LoginInfoSlice);

  const JoinHalligalli = useCallback(() => {
    const docRef = doc(db, "game", `room${roomId}`);
    updateDoc(docRef, {
      "halligalli.halligalliUsers": arrayUnion({
        userId,
        nickname,
        cards: [],
        end: false,
        front: [],
      }),
    });

    // updateDoc(docRef, {
    //   "halligalli.halligalliUsers": arrayUnion({
    //     userId: "test1",
    //     nickname: "test1",
    //     cards: [],
    //     end: false,
    //     front: [],
    //   }),
    // });
  }, [roomId, userId, nickname]);

  const ExitHalligalli = useCallback(() => {
    const docRef = doc(db, "game", `room${roomId}`);

    const remainUsers = halligalliUsers.filter(i => i?.userId !== userId);

    updateDoc(docRef, {
      "halligalli.halligalliUsers": remainUsers,
    });
  }, [roomId, halligalliUsers, userId]);

  const StartHalligalli = useCallback(() => {
    const users = halligalliUsers.filter(
      i => !i?.userId.includes("HALLIGALLIBOT")
    );

    let updateUsers = [];
    let cards = initialDecks();
    const totalCardLength = cards.length;

    if (users.length > 1) {
      halligalliUsers.forEach(item => {
        const newItem = { ...item };
        updateUsers.push(newItem);
      });

      updateUsers.forEach((_, index) => {
        const cardLength = Math.floor(totalCardLength / updateUsers.length);
        updateUsers[index].cards = cards.splice(0, cardLength);
      });

      if (cards.length) {
        const bonusUser = getRandomUniqueNumbers(
          updateUsers.length,
          cards.length
        );
        cards.forEach((item, index) => {
          updateUsers[bonusUser[index]].cards.push(item);
        });
      }
      const docRef = doc(db, "game", `room${roomId}`);

      const hostUser = users.find(i => i?.userId === userId);

      if (hostUser) {
        updateDoc(docRef, {
          "halligalli.halligalliUsers": updateUsers,
          "halligalli.gameStatus": true,
          "halligalli.turnUser": halligalliUsers[0],
          "halligalli.hostUser": hostUser,
          "halligalli.turnStep": 1,
        });
      } else {
        toast.error("게임에 참여하지 않았습니다");
      }
    } else {
      toast.error("인원이 모자랍니다.");
    }
  }, [roomId, halligalliUsers, userId]);

  const OpenCard = useCallback(() => {
    if (turnUser?.userId === userId && turnStep === 1) {
      let updateUsers = [];
      halligalliUsers.forEach(item => {
        const newItem = { ...item };
        newItem.cards = [...newItem?.cards];
        newItem.front = [...newItem?.front];
        if (newItem.userId === userId) {
          const [pushCard] = newItem.cards.splice(0, 1);
          newItem?.front.push(pushCard);
        }
        updateUsers.push(newItem);
      });
      const docRef = doc(db, "game", `room${roomId}`);

      const findIndex = halligalliUsers.findIndex(i => i?.userId === userId);
      const nextIndex = getNextIndex(findIndex, halligalliUsers);

      const nextUser = halligalliUsers[nextIndex];

      updateDoc(docRef, {
        "halligalli.halligalliUsers": updateUsers,
        "halligalli.turnUser": nextUser,
        "halligalli.gameTurn": increment(1),
      });
    } else {
      toast.error("카드를 뒤집을 수 없습니다.");
    }
  }, [roomId, turnUser, userId, halligalliUsers, turnStep]);

  const PressBell = useCallback(async () => {
    if (gameStatus) {
      const docRef = doc(db, "game", `room${roomId}`);
      const lastOrder = (message[0]?.order || 0) + 1;

      const bellPressItem = {
        userId,
        nickname,
        captureBoard: { halligalliUsers, turnUser },
      };

      const newMessage = {
        text: `님이 벨을 눌렀습니다.`,
        order: lastOrder,
        userId,
        nickname,
        type: "system",
        date: formatTime(new Date()),
      };

      updateDoc(docRef, {
        "halligalli.turnStep": 2,
        "halligalli.bellPress": arrayUnion(bellPressItem),
        message: arrayUnion(newMessage),
      });
    } else {
      toast.error("벨을 누를수 없습니다.");
    }
  }, [
    roomId,
    gameStatus,
    userId,
    nickname,
    halligalliUsers,
    turnUser,
    message,
  ]);
  const checkPressAnswer = useMemo(() => {
    const checkItem = bellPress[0];
    const lastBoards = checkItem?.captureBoard;
    const lastFronts = lastBoards?.halligalliUsers?.map(i => i?.front);
    const lastCards = lastFronts?.map(i => i?.[i?.length - 1]);
    const cardFilter = lastCards?.filter(i => i);
    return isSumExactlyFive(cardFilter);
  }, [bellPress]);

  const ShareCards = useCallback(() => {
    const docRef = doc(db, "game", `room${roomId}`);

    const checkItem = bellPress[0];
    const lastBoards = checkItem?.captureBoard;
    let copyBoard = deepCopy(lastBoards);

    let lastUsers = copyBoard?.halligalliUsers;
    // end가 false거나, front의 길이가 있으면 살아있음
    let lives = lastUsers?.filter(i => !i?.end);

    const lastUser = copyBoard?.turnUser;

    const findIndex = lastUsers?.findIndex(i => i?.userId === userId);

    const lastOrder = message[0]?.order + 1 || 1;
    const newMessage = {
      text: `님이 1장씩 카드 지급`,
      order: lastOrder,
      userId,
      nickname,
      type: "system",
      date: formatTime(new Date()),
    };

    if (lastUsers?.[findIndex]?.cards.length < lives.length) {
      lastUsers[findIndex].front = [
        ...lastUsers[findIndex]?.cards,
        ...lastUsers[findIndex]?.front,
      ];
      lastUsers[findIndex].end = true;
      lastUsers[findIndex].cards = [];
      // 사망처리, 남은 카드 프론트로
    } else {
      lastUsers.forEach((item, index) => {
        if (item?.userId === userId || item?.end) {
          return;
        }
        const [pushCard] = lastUsers?.[findIndex]?.cards.splice(0, 1);

        lastUsers?.[index]?.cards?.push(pushCard);
      });
      // 남은 카드 에서 한장씩 살아있는 나머지에게 추가
    }

    const step =
      lastUsers?.filter(i => i?.end)?.length === lastUsers?.length - 1 ? 3 : 1;

    updateDoc(docRef, {
      "halligalli.turnStep": step,
      "halligalli.bellPress": [],
      "halligalli.turnUser": lastUser,
      "halligalli.halligalliUsers": lastUsers,
      "halligalli.gameTurn": increment(1),
      message: arrayUnion(newMessage),
    });

    // 틀렸을 경우에 본인이 가지고 있는 카드를 한장씩 나눠준다.
    // 나머지 인원에게 나눠준다.
    // 나머지 인원중 front가 있거나 end가 아닌 애들에게 나눠준다.
    // 만약 나눠줄 카드가 나눠줄 인원수보다 적다면 남은카드 front로 보내고 end 처리 후에 turnStep => 1,nextUser로 넘긴다
  }, [roomId, bellPress, userId, message, nickname]);

  const BringCard = useCallback(() => {
    const docRef = doc(db, "game", `room${roomId}`);

    const checkItem = bellPress[0];
    const lastBoards = checkItem?.captureBoard;
    let copyBoard = deepCopy(lastBoards);
    let lastUsers = copyBoard?.halligalliUsers;
    const lastUser = copyBoard?.turnUser;
    const lastIndex = lastUsers?.findIndex(i => i?.userId === lastUser?.userId);
    const selfIndex = lastUsers?.findIndex(i => i?.userId === userId);
    let totalFront = [];
    // 모든 front들을 합쳐서 나의 cards에 추가 후 셔플
    lastUsers?.forEach((item, index) => {
      totalFront = [...totalFront, ...item?.front];
      lastUsers[index].front = [];
      if (lastUsers[index].cards.length < 1) {
        lastUsers[index].end = true;
      }
    });
    lastUsers[selfIndex].cards = shuffle([
      ...lastUsers[selfIndex].cards,
      ...totalFront,
    ]);
    const step =
      lastUsers?.filter(i => i?.end)?.length === lastUsers?.length - 1 ? 3 : 1;

    const nextIndex = getNextIndex(lastIndex, lastUsers);
    const nextUser = lastUsers[lastIndex]?.end
      ? lastUsers[nextIndex]
      : lastUser;

    const lastOrder = message[0]?.order + 1 || 1;
    const newMessage = {
      text: `님이 카드 ‘${totalFront.length}장’ 획득`,
      order: lastOrder,
      userId,
      nickname,
      type: "system",
      date: formatTime(new Date()),
    };

    updateDoc(docRef, {
      "halligalli.turnStep": step,
      "halligalli.bellPress": [],
      "halligalli.turnUser": nextUser,
      "halligalli.halligalliUsers": lastUsers,
      "halligalli.gameTurn": increment(1),
      message: arrayUnion(newMessage),
    });
  }, [bellPress, roomId, userId, message, nickname]);

  const ResetGame = useCallback(() => {
    const docRef = doc(db, "game", `room${roomId}`);
    updateDoc(docRef, {
      halligalli,
    });
  }, [roomId]);

  return {
    checkPressAnswer,
    JoinHalligalli,
    ExitHalligalli,
    StartHalligalli,
    OpenCard,
    PressBell,
    ShareCards,
    BringCard,
    ResetGame,
  };
};

export default HalligalliHooks;

const getNextIndex = (index, arr) => {
  let nexts = { 4: 0, 0: 2, 2: 5, 5: 1, 1: 3, 3: 4 };
  let next = index;
  while (
    !arr[nexts[next]] ||
    !arr[nexts[next]]?.cards?.length ||
    arr[nexts[next]]?.end
  ) {
    next = nexts[next];
  }
  return nexts[next];
};

const initialDecks = () => {
  let arr = [];
  const fruitList = ["banana", "strawberry", "lime", "cherry"];
  const countList = [5, 3, 3, 2, 1];
  fruitList.forEach((item, index) => {
    // 바나나를 1개는 5개, 2개는 3개
    countList.forEach((count, fruitCount) => {
      for (let j = 0; j < count; j++) {
        arr.push({
          label: item,
          num: fruitCount + 1,
        });
      }
    });
  });
  return shuffle(arr);
};

function getRandomUniqueNumbers(n, x) {
  const numbers = Array.from({ length: n + 1 }, (_, i) => i);

  // 배열을 랜덤하게 섞습니다.
  for (let i = numbers.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [numbers[i], numbers[j]] = [numbers[j], numbers[i]];
  }

  // 앞에서 x개의 숫자를 추출합니다.
  return numbers.slice(0, x);
}

function isSumExactlyFive(arr) {
  // label 별로 value를 합산하기 위한 객체 생성
  const labelSums = {};

  // 배열을 순회하면서 label 별로 value를 합산
  arr?.forEach(({ label, num }) => {
    if (!labelSums[label]) {
      labelSums[label] = 0;
    }
    labelSums[label] += num;
  });

  // 동일한 label의 value 합이 5인 경우가 있는지 확인
  for (const label in labelSums) {
    if (labelSums[label] === 5) {
      return true;
    }
  }

  return false;
}
